/**
 * Tool Categorization for Rendering Strategies
 *
 * This module provides utilities for categorizing tools and generating
 * display text for inline tool representations.
 *
 * Re-exports the unified tool configuration for backward compatibility
 * and adds inline-specific display text generation.
 */

import {
  TERMINAL_TOOL_NAMES,
  TERMINAL_TOOL_PRESENTATION,
} from '@terra/shared-dto-chat-types';
import { clientFacingToolName } from '../client-facing-tool-name';
import { summarizeSelectedToolNames } from '../../trace-view/utils/load-tools-output';
import { getSkillDisplayNameFromPath } from './skill-display';

// =============================================================================
// Re-exports from unified tool-config
// =============================================================================

export {
  type ToolCategory,
  type ToolConfig,
  getToolConfig,
} from '../tool-config';

// =============================================================================
// Type Guards and Utilities
// =============================================================================

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getStringProp(input: unknown, key: string): string {
  if (!isRecord(input)) return '';
  const value = input[key];

  return typeof value === 'string' ? value : '';
}

function getFilePath(input: unknown): string {
  return (
    getStringProp(input, 'filePath') ||
    getStringProp(input, 'file_path') ||
    getStringProp(input, 'targetFile') ||
    getStringProp(input, 'target_file') ||
    getStringProp(input, 'filename') ||
    getStringProp(input, 'path')
  );
}

function truncate(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T/;

function formatFilterValue(value: string): string {
  if (ISO_DATE_PATTERN.test(value)) {
    return value.slice(0, 10);
  }

  return truncate(value, 30);
}

function formatArrayItem(item: unknown): string {
  if (item === null || item === undefined) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'number' || typeof item === 'boolean') {
    return String(item);
  }

  if (isRecord(item)) {
    if (typeof item.field === 'string' && typeof item.order === 'string') {
      return `${item.field} ${item.order}`;
    }

    try {
      return JSON.stringify(item);
    } catch {
      return '';
    }
  }

  return '';
}

function getFiltersDescription(input: unknown): string {
  if (!isRecord(input)) return '';
  const filters = input['filters'];

  if (!isRecord(filters)) return '';

  const parts: string[] = [];

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length > 0) {
      const formatted = value
        .map(formatArrayItem)
        .filter((item) => item.length > 0)
        .join(', ');

      if (formatted.length > 0) {
        parts.push(`${key}: ${formatted}`);
      }
    } else if (typeof value === 'boolean') {
      parts.push(`${key}: ${value ? 'yes' : 'no'}`);
    } else if (typeof value === 'string' && value.length > 0) {
      parts.push(`${key}: ${formatFilterValue(value)}`);
    } else if (typeof value === 'number') {
      parts.push(`${key}: ${value}`);
    }
  }

  if (parts.length === 0) return '';

  return `(${parts.join(' · ')})`;
}

// =============================================================================
// Sub-agent Display Names
// =============================================================================

const SUB_AGENT_DISPLAY_NAMES: Record<string, string> = {
  validator: 'Validator',
  operator: 'Conductor',
  intelligence: 'Researcher',
  'signal-creator': 'Signal Creator',
};

export function getSubAgentDisplayName(toolName?: string): string {
  if (!toolName) return 'Sub-agent';

  return SUB_AGENT_DISPLAY_NAMES[toolName] || toolName;
}

// =============================================================================
// Inline Tool Display Text
// =============================================================================

type InlineTextConfig = {
  inProgressVerb: string;
  completedVerb: string;
  getDetails?: (input: unknown) => string;
};

const INLINE_TEXT_CONFIGS: Record<string, InlineTextConfig> = {
  // File operations
  'read-file': {
    inProgressVerb: 'Reading',
    completedVerb: 'Read',
    getDetails: (input) => {
      const filePath = getFilePath(input);
      const skillName = getSkillDisplayNameFromPath(filePath);

      if (skillName) {
        return `skill ${skillName}`;
      }

      return `file ${filePath}`;
    },
  },
  'write-file': {
    inProgressVerb: 'Writing',
    completedVerb: 'Wrote',
    getDetails: (input) => `file ${getStringProp(input, 'filePath')}`,
  },
  ls: {
    inProgressVerb: 'Listing',
    completedVerb: 'Listed',
    getDetails: (input) => `directory ${getStringProp(input, 'path') || '.'}`,
  },
  'load-tools': {
    inProgressVerb: 'Loading',
    completedVerb: 'Loaded',
    getDetails: (input) =>
      (isRecord(input)
        ? summarizeSelectedToolNames({
            args: input,
            formatName: clientFacingToolName,
          })
        : undefined) ?? 'tools',
  },

  // Search tools
  'websearch-tool': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: (input) => getStringProp(input, 'query'),
  },
  'findings-tool': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'findings',
  },
  'search-tracked-endpoints': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'endpoints',
  },
  'get-tracked-endpoint-schema': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'endpoint details',
  },
  'search-tracked-web-pages': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'web pages',
  },
  'list-assets-tool': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'assets',
  },
  'get-tracked-web-page': {
    inProgressVerb: 'Reading',
    completedVerb: 'Read',
    getDetails: () => 'web page',
  },
  'list-web-pages-for-endpoint': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'endpoint webpages',
  },
  'list-endpoints-on-web-page': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'webpage endpoints',
  },
  'feature-analysis-tool': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'product features',
  },
  'signals-semantic-search-tool': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: (input) => {
      const query = getStringProp(input, 'query');

      return query ? `signals: ${query}` : 'signals';
    },
  },
  'finding-semantic-search-tool': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: (input) => {
      const query = getStringProp(input, 'query');

      return query ? `findings: ${query}` : 'findings';
    },
  },
  'findings-semantic-search-tool': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: (input) => {
      const query = getStringProp(input, 'query');

      return query ? `findings: ${query}` : 'findings';
    },
  },
  'long-term-memory-tool': {
    inProgressVerb: 'Recalling',
    completedVerb: 'Recalled',
    getDetails: () => 'memory',
  },
  'get-memory-tool': {
    inProgressVerb: 'Recalling',
    completedVerb: 'Recalled',
    getDetails: (input) => {
      const query = getStringProp(input, 'query');

      return query ? `memory: ${query}` : 'memory';
    },
  },
  get_neo_memories: {
    inProgressVerb: 'Recalling',
    completedVerb: 'Recalled',
    getDetails: (input) => {
      const query = getStringProp(input, 'query');

      return query ? `memory: ${query}` : 'memory';
    },
  },
  'har-entries-tool': {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: () => 'HAR entries',
  },
  'signals-explorer-tool': {
    inProgressVerb: 'Searching for',
    completedVerb: 'Searched',
    getDetails: (input) => {
      const parts = ['signals'];
      const filters = getFiltersDescription(input);

      if (filters) parts.push(filters);

      return parts.join(' ');
    },
  },
  'findings-explorer-tool': {
    inProgressVerb: 'Searching for',
    completedVerb: 'Searched',
    getDetails: (input) => {
      const parts = ['findings'];
      const filters = getFiltersDescription(input);

      if (filters) parts.push(filters);

      return parts.join(' ');
    },
  },
  'endpoints-explorer-tool': {
    inProgressVerb: 'Searching for',
    completedVerb: 'Searched',
    getDetails: (input) => {
      const parts = ['endpoints'];
      const filters = getFiltersDescription(input);

      if (filters) parts.push(filters);

      return parts.join(' ');
    },
  },
  'executions-explorer-tool': {
    inProgressVerb: 'Searching for',
    completedVerb: 'Searched',
    getDetails: (input) => {
      const parts = ['executions'];
      const filters = getFiltersDescription(input);

      if (filters) parts.push(filters);

      return parts.join(' ');
    },
  },
  'har-explorer-tool': {
    inProgressVerb: 'Searching for',
    completedVerb: 'Searched',
    getDetails: (input) => {
      const parts = ['HAR entries'];
      const filters = getFiltersDescription(input);

      if (filters) parts.push(filters);

      return parts.join(' ');
    },
  },

  // Analysis tools
  'business-context-tool': {
    inProgressVerb: 'Querying',
    completedVerb: 'Queried',
    getDetails: () => 'business context',
  },
  'application-scoping-tool': {
    inProgressVerb: 'Analyzing',
    completedVerb: 'Analyzed',
    getDetails: () => 'application scope',
  },
  'code-analyzer': {
    inProgressVerb: 'Analyzing',
    completedVerb: 'Analyzed',
    getDetails: () => 'code',
  },
  'code-analyzer-tool': {
    inProgressVerb: 'Analyzing',
    completedVerb: 'Analyzed',
    getDetails: () => 'code',
  },
  grep: {
    inProgressVerb: 'Searching',
    completedVerb: 'Searched',
    getDetails: (input) => getStringProp(input, 'summary') || 'code',
  },
  read: {
    inProgressVerb: 'Reading',
    completedVerb: 'Read',
    getDetails: (input) => getStringProp(input, 'summary') || 'file',
  },
  glob: {
    inProgressVerb: 'Listing',
    completedVerb: 'Listed',
    getDetails: (input) => getStringProp(input, 'summary') || 'files',
  },
  bash: {
    inProgressVerb: 'Running',
    completedVerb: 'Ran',
    getDetails: (input) => getStringProp(input, 'summary') || 'command',
  },
  'signal-latest-execution-summary-tool': {
    inProgressVerb: 'Getting',
    completedVerb: 'Got',
    getDetails: () => 'execution summary',
  },
  ...Object.fromEntries(
    TERMINAL_TOOL_NAMES.map((toolName) => [
      toolName,
      {
        inProgressVerb: TERMINAL_TOOL_PRESENTATION[toolName].inProgressVerb,
        completedVerb: TERMINAL_TOOL_PRESENTATION[toolName].completedVerb,
        getDetails: (input: unknown) => {
          const command = getStringProp(input, 'command');
          const jobId = getStringProp(input, 'jobId');
          const details = command || jobId;

          return details ? `\`${truncate(details, 50)}\`` : '';
        },
      },
    ]),
  ),
};

/**
 * Generate display text for inline tool representation
 *
 * @param toolName - The name of the tool
 * @param input - The tool input arguments
 * @param status - Current execution status ('started', 'in-progress', 'completed', etc.)
 * @returns Human-readable text describing the tool action
 */
export function getToolDisplayTextForInline(
  toolName?: string,
  input?: unknown,
  status?: string,
): string {
  if (!toolName) return 'Unknown tool';

  const normalizedToolName = toolName.replace(/_/g, '-').toLowerCase();

  if (normalizedToolName === 'read-file') {
    const skillName = getSkillDisplayNameFromPath(getFilePath(input));

    if (skillName) {
      return `Reading skill ${skillName}`;
    }

    return 'Reading skill';
  }

  const config =
    INLINE_TEXT_CONFIGS[toolName] ?? INLINE_TEXT_CONFIGS[normalizedToolName];

  if (!config) {
    return normalizedToolName
      .replace(/-tool$/, '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  const isInProgress = status === 'started' || status === 'in-progress';
  const verb = isInProgress ? config.inProgressVerb : config.completedVerb;
  const details = config.getDetails?.(input) || '';

  return details ? `${verb} ${details}` : verb;
}
